import {
  getCoreRowModel,
  useReactTable,
  type ColumnDef,
  type OnChangeFn,
  type RowSelectionState,
  type SortingState,
  type VisibilityState,
} from '@tanstack/react-table';
import type { DataTableRow } from '@/shared/ui/data-table/types';

/** Everything the TanStack instance is built from. */
export type TableBuildInput<TData extends DataTableRow> = {
  data: TData[];
  columns: ColumnDef<TData, unknown>[];
  rowKey: (row: TData) => string;
  pageCount: number;
  pageIndex: number;
  pageSize: number;
  selectable: boolean;
  sorting: SortingState;
  columnVisibility: VisibilityState;
  rowSelection: RowSelectionState;
  onSortingChange: OnChangeFn<SortingState>;
  onColumnVisibilityChange: OnChangeFn<VisibilityState>;
  onRowSelectionChange: OnChangeFn<RowSelectionState>;
  /**
   * Whether a third click clears the sort. Default true (TanStack's default).
   * A controlled sort display passes false so the click cycle stays
   * ascending → descending → ascending, matching a two-state sort contract.
   */
  enableSortingRemoval?: boolean;
};

/**
 * Wraps `useReactTable` so the parent's complexity budget is not consumed by a
 * 14-field config object. No client row models are registered — sorting,
 * filtering and pagination are all `manual` — so the table can never silently
 * re-slice the page it was handed.
 */
export function useTableInstance<TData extends DataTableRow>(input: TableBuildInput<TData>) {
  return useReactTable<TData>({
    data: input.data,
    columns: input.columns,
    getRowId: input.rowKey,
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    pageCount: input.pageCount,
    state: {
      sorting: input.sorting,
      columnVisibility: input.columnVisibility,
      rowSelection: input.rowSelection,
      pagination: { pageIndex: input.pageIndex, pageSize: input.pageSize },
    },
    onSortingChange: input.onSortingChange,
    onColumnVisibilityChange: input.onColumnVisibilityChange,
    onRowSelectionChange: input.onRowSelectionChange,
    enableSortingRemoval: input.enableSortingRemoval ?? true,
    enableRowSelection: input.selectable,
  });
}
